import React, { useState, useEffect, useCallback } from 'react';
import { PageContainer, PageHeader } from '@/components/layout';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertCircle,
  Users,
  Building2,
  ChevronDown,
  ChevronUp,
  Search,
  TrendingUp,
  Calendar,
  UserCircle,
  Trash2,
  Edit3,
} from 'lucide-react';
import {
  teamApi, TeamSummary, TeamMember, TeamUsage,
} from '@/api/teamApi';
import { PlanCode } from '@/api/planApi';
import { LoadingBlock, Spinner } from '@/components/ui/spinner';

// ── 상수 ─────────────────────────────────────────────────────

const PLAN_NAMES: Record<PlanCode, string> = {
  FREE: '무료',
  STARTER: '스타트',
  PROFESSIONAL: '프로',
  ENTERPRISE: '엔터프라이즈',
};

const PLAN_COLORS: Record<PlanCode, string> = {
  FREE: 'bg-gray-100 text-gray-600 border-gray-200',
  STARTER: 'bg-blue-100 text-blue-600 border-blue-200',
  PROFESSIONAL: 'bg-purple-100 text-purple-600 border-purple-200',
  ENTERPRISE: 'bg-amber-100 text-amber-600 border-amber-200',
};

const STATUS_LABELS: Record<string, string> = {
  ACTIVE: '활성',
  PENDING: '대기',
  SUSPENDED: '정지',
  INACTIVE: '비활성',
};

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: 'bg-green-100 text-green-600 border-green-200',
  PENDING: 'bg-yellow-100 text-yellow-600 border-yellow-200',
  SUSPENDED: 'bg-red-100 text-red-600 border-red-200',
  INACTIVE: 'bg-gray-100 text-gray-500 border-gray-200',
};

const PAGE_SIZE = 20;

// ── 컴포넌트 ─────────────────────────────────────────────────

export default function AdminTeamList() {
  const [teams, setTeams] = useState<TeamSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [planFilter, setPlanFilter] = useState<PlanCode | 'ALL'>('ALL');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [expandedTeamId, setExpandedTeamId] = useState<number | null>(null);

  // 상세 정보 상태
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [usage, setUsage] = useState<TeamUsage | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // 삭제 확인 모달
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<TeamSummary | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await teamApi.getAdminTeams();
      // axios 인터셉터 변환으로 인해 res.data가 이미 {content: [...]} 형태
      const data = Array.isArray(res.data) 
        ? res.data 
        : (res.data as any)?.content ?? [];
      setTeams(data);
    } catch (err) {
      setError('팀 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadDetail = useCallback(async (teamId: number) => {
    setDetailLoading(true);
    try {
      const [membersRes, usageRes] = await Promise.all([
        teamApi.getMembers(teamId),
        teamApi.getUsage(teamId),
      ]);
      setMembers(membersRes.data);
      setUsage(usageRes.data);
    } catch {
      setError('팀 상세 정보를 불러오는데 실패했습니다.');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const handleToggleExpand = (teamId: number) => {
    if (expandedTeamId === teamId) {
      setExpandedTeamId(null);
    } else {
      setExpandedTeamId(teamId);
      loadDetail(teamId);
    }
  };

  const handleOpenDeleteDialog = (team: TeamSummary) => {
    setSelectedTeam(team);
    setShowDeleteDialog(true);
  };

  const handleDeleteTeam = async () => {
    if (!selectedTeam) return;
    setDeleting(true);
    try {
      await teamApi.deleteTeamAdmin(selectedTeam.teamId);
      await load();
      setExpandedTeamId(null);
      setShowDeleteDialog(false);
    } catch {
      setError('팀 삭제에 실패했습니다.');
    } finally {
      setDeleting(false);
    }
  };

  // 필터링
  const filteredTeams = teams.filter(team => {
    const matchesSearch = !searchQuery ||
      team.teamName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      team.ownerUserId?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPlan = planFilter === 'ALL' || team.plan === planFilter;
    const matchesStatus = statusFilter === 'ALL' || team.status === statusFilter;
    return matchesSearch && matchesPlan && matchesStatus;
  });
  
  console.log('Filtered teams:', filteredTeams);
  console.log('Teams state:', teams);
  console.log('Filters:', { searchQuery, planFilter, statusFilter });

  const formatDate = (iso: string) =>
    iso ? new Date(iso).toLocaleDateString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    }) : '-';

  return (
    <PageContainer maxWidth="full">
      <PageHeader 
        title="팀 관리"
        description="모든 팀의 정보와 사용량, 이용 현황을 한눈에 확인합니다."
        action={
          <Badge className="bg-blue-100 text-blue-700 border border-blue-300 text-sm px-3 py-1">
            총 {teams.length.toLocaleString()}개 팀
          </Badge>
        }
      />

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* 필터 */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="팀명 또는 팀장 ID 검색"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex gap-1">
              {(['ALL', 'FREE', 'STARTER', 'PROFESSIONAL', 'ENTERPRISE'] as const).map((plan) => (
                <Button
                  key={plan}
                  variant={planFilter === plan ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPlanFilter(plan)}
                  className="text-xs"
                >
                  {plan === 'ALL' ? '전체' : PLAN_NAMES[plan]}
                </Button>
              ))}
            </div>
            <div className="flex gap-1">
              {(['ALL', 'ACTIVE', 'PENDING', 'SUSPENDED', 'INACTIVE'] as const).map((status) => (
                <Button
                  key={status}
                  variant={statusFilter === status ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setStatusFilter(status)}
                  className="text-xs"
                >
                  {status === 'ALL' ? '전체 상태' : STATUS_LABELS[status]}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 팀 목록 */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <LoadingBlock size="lg" py={16} />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-10"></TableHead>
                  <TableHead>팀명</TableHead>
                  <TableHead>팀장</TableHead>
                  <TableHead>요금제</TableHead>
                  <TableHead className="text-center">팀원</TableHead>
                  <TableHead className="text-center">상태</TableHead>
                  <TableHead className="text-right">이번달 사용량</TableHead>
                  <TableHead>이번 달 사용 기간</TableHead>
                  <TableHead className="text-center">관리</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTeams.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-gray-400 py-16 text-sm">
                      팀이 없습니다.
                    </TableCell>
                  </TableRow>
                ) : filteredTeams.map((team) => {
                  const isExpanded = expandedTeamId === team.teamId;
                  return (
                    <React.Fragment key={team.teamId}>
                      <TableRow className="cursor-pointer hover:bg-gray-50" onClick={() => handleToggleExpand(team.teamId)}>
                        <TableCell>
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{team.teamName}</TableCell>
                        <TableCell className="text-gray-600 text-xs">{team.ownerUserId ?? '-'}</TableCell>
                        <TableCell>
                          {team.plan && (
                            <Badge className={`text-xs border ${PLAN_COLORS[team.plan as PlanCode] ?? 'bg-gray-100 text-gray-600'}`}>
                              {PLAN_NAMES[team.plan as PlanCode] ?? team.plan}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="flex items-center justify-center gap-1 text-sm">
                            <Users className="w-3.5 h-3.5 text-gray-400" />
                            {team.memberCount ?? 0}명
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          {team.status && (
                            <Badge className={`text-xs border ${STATUS_COLORS[team.status] ?? 'bg-gray-100 text-gray-600'}`}>
                              {STATUS_LABELS[team.status] ?? team.status}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          {team.monthlyLimit ? (
                            <span className={usage && usage.teamId === team.teamId && usage.totalUsed >= team.monthlyLimit ? 'text-red-600 font-medium' : ''}>
                              {usage?.teamId === team.teamId ? usage.totalUsed.toLocaleString() : '-'}
                              /{team.monthlyLimit.toLocaleString()}건
                            </span>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-gray-500">
                          {usage?.teamId === team.teamId && usage.billingStartDate && usage.billingEndDate ? (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(usage.billingStartDate)} ~ {formatDate(usage.billingEndDate)}
                            </span>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-red-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDeleteDialog(team);
                              }}
                              title="팀 삭제"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>

                      {/* 상세 패널 */}
                      {isExpanded && (
                        <TableRow>
                          <TableCell colSpan={9} className="bg-gray-50 p-0">
                            <div className="p-4">
                              {detailLoading ? (
                                <LoadingBlock size="md" py={8} />
                              ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {/* 팀원 목록 */}
                                  <Card>
                                    <CardHeader className="pb-2">
                                      <h4 className="text-sm font-semibold flex items-center gap-2">
                                        <Users className="w-4 h-4 text-gray-500" />
                                        팀원 목록 ({members.length}명)
                                      </h4>
                                    </CardHeader>
                                    <CardContent className="pt-0">
                                      <div className="space-y-2 max-h-48 overflow-y-auto">
                                        {members.map((member) => (
                                          <div
                                            key={member.userId}
                                            className="flex items-center justify-between p-2 bg-white rounded border"
                                          >
                                            <div className="flex items-center gap-2">
                                              <UserCircle className="w-4 h-4 text-gray-400" />
                                              <div>
                                                <p className="text-sm font-medium">{member.name}</p>
                                                <p className="text-xs text-gray-500">{member.email}</p>
                                              </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                              <Badge
                                                className={`text-xs ${member.role === 'OWNER' ? 'bg-gray-800 text-white' : 'bg-white border text-gray-600'}`}
                                              >
                                                {member.role === 'OWNER' ? '팀장' : '팀원'}
                                              </Badge>
                                              {usage?.memberStats.find(m => m.userId === member.userId) && (
                                                <span className="text-xs text-gray-500">
                                                  {usage.memberStats.find(m => m.userId === member.userId)?.usedCount.toLocaleString()}건
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </CardContent>
                                  </Card>

                                  {/* 사용량 및 이용 정보 */}
                                  <Card>
                                    <CardHeader className="pb-2">
                                      <h4 className="text-sm font-semibold flex items-center gap-2">
                                        <TrendingUp className="w-4 h-4 text-gray-500" />
                                        사용량 및 이용 정보
                                      </h4>
                                    </CardHeader>
                                    <CardContent className="pt-0 space-y-3">
                                      {usage && (
                                        <>
                                          <div className="grid grid-cols-2 gap-3">
                                            <div className="bg-white p-3 rounded border text-center">
                                              <p className="text-xs text-gray-500">이번달 사용량</p>
                                              <p className="text-lg font-bold text-gray-800">
                                                {usage.totalUsed.toLocaleString()}건
                                              </p>
                                            </div>
                                            <div className="bg-white p-3 rounded border text-center">
                                              <p className="text-xs text-gray-500">남은 건수</p>
                                              <p className={`text-lg font-bold ${usage.remaining < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                                {usage.remaining.toLocaleString()}건
                                              </p>
                                            </div>
                                          </div>
                                          <div className="bg-white p-3 rounded border">
                                            <p className="text-xs text-gray-500 mb-1">이번 달 사용 기간</p>
                                            <p className="text-sm font-medium">
                                              {usage.billingStartDate && usage.billingEndDate ? (
                                                <span className="flex items-center gap-1">
                                                  <Calendar className="w-3.5 h-3.5 text-gray-400" />
                                                  {formatDate(usage.billingStartDate)} ~ {formatDate(usage.billingEndDate)}
                                                </span>
                                              ) : (
                                                '-'
                                              )}
                                            </p>
                                          </div>
                                          <div className="bg-white p-3 rounded border">
                                            <p className="text-xs text-gray-500 mb-2">팀원별 사용량</p>
                                            <div className="space-y-1">
                                              {usage.memberStats.slice(0, 5).map((stat) => (
                                                <div key={stat.userId} className="flex items-center justify-between text-sm">
                                                  <span className="text-gray-600">{stat.name}</span>
                                                  <span className="font-medium">{stat.usedCount.toLocaleString()}건</span>
                                                </div>
                                              ))}
                                              {usage.memberStats.length > 5 && (
                                                <p className="text-xs text-gray-400 text-center">
                                                  외 {usage.memberStats.length - 5}명 더보기...
                                                </p>
                                              )}
                                            </div>
                                          </div>
                                        </>
                                      )}
                                    </CardContent>
                                  </Card>
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 팀 삭제 확인 다이얼로그 */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              팀 삭제
            </DialogTitle>
            <DialogDescription>
              <strong>{selectedTeam?.teamName}</strong> 팀을 삭제하시겠습니까?<br />
              <span className="text-red-600">이 작업은 되돌릴 수 없습니다.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 mt-4">
            <Button
              variant="destructive"
              onClick={handleDeleteTeam}
              disabled={deleting}
              className="flex-1"
            >
              {deleting ? <Spinner size="sm" /> : '삭제'}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={deleting}
              className="flex-1"
            >
              취소
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
